/**
 * Push Notifications Hook
 *
 * Handles FCM push notification registration and listening for Capacitor apps.
 * - Requests permission
 * - Registers device token with backend
 * - Handles notification clicks (deep linking)
 */

import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '../lib/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

export function usePushNotifications(user, userRole) {
  const navigate = useNavigate();
  const registeredRef = useRef(false);

  // Register device token with backend
  const registerToken = useCallback(async (token) => {
    if (!user) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`${API_URL}/notifications/register-device`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          device_token: token,
          device_type: Capacitor.getPlatform(), // 'android', 'ios', or 'web'
          device_name: navigator.userAgent.substring(0, 100)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to register device token');
      }

      console.log('[PushNotifications] Device token registered successfully');

    } catch (error) {
      console.error('[PushNotifications] Failed to register token:', error);
    }
  }, [user]);

  // Handle notification click - navigate to relevant page
  const handleNotificationClick = useCallback((notification) => {
    console.log('[PushNotifications] Notification clicked:', notification);

    const data = notification.data || {};

    // Navigate based on notification type
    switch (data.notification_type) {
      case 'new_rfq':
      case 'rfq_invited':
      case 'rfq_inclusion_requested':
        if (data.rfq_id) {
          navigate(`/recruiter/rfqs/${data.rfq_id}`);
        } else {
          navigate('/recruiter/rfqs');
        }
        break;

      case 'new_application':
        if (data.application_id) {
          // Navigate to application or RFQ with application
          navigate(`/recruiter/rfqs/${data.rfq_id}`);
        } else {
          navigate('/recruiter/rfqs');
        }
        break;

      case 'candidate_request':
        navigate('/recruiter/requests');
        break;

      default:
        // Default to notifications inbox
        navigate('/recruiter/notifications');
    }
  }, [navigate]);

  // Initialize push notifications
  useEffect(() => {
    // Only run on native platforms (Android/iOS)
    if (!Capacitor.isNativePlatform()) {
      console.log('[PushNotifications] Not a native platform, skipping');
      return;
    }

    // Only register for recruiters/admins
    if (!user || !['recruiter', 'admin'].includes(userRole)) {
      console.log('[PushNotifications] User not eligible for push notifications');
      return;
    }

    // Prevent double registration
    if (registeredRef.current) {
      return;
    }

    const initPushNotifications = async () => {
      try {
        // Check/request permission
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
          console.log('[PushNotifications] Permission not granted');
          return;
        }

        // Register with FCM
        await PushNotifications.register();
        registeredRef.current = true;

        console.log('[PushNotifications] Registered with FCM');

      } catch (error) {
        console.error('[PushNotifications] Initialization error:', error);
      }
    };

    // Set up listeners
    const setupListeners = async () => {
      // On registration success - get the token
      await PushNotifications.addListener('registration', (token) => {
        console.log('[PushNotifications] FCM Token:', token.value);
        registerToken(token.value);
      });

      // On registration error
      await PushNotifications.addListener('registrationError', (error) => {
        console.error('[PushNotifications] Registration error:', error);
      });

      // When notification is received while app is in foreground
      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.log('[PushNotifications] Notification received in foreground:', notification);
        // The NotificationContext will handle showing the toast via Supabase Realtime
        // This is just for logging
      });

      // When user taps on a notification
      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.log('[PushNotifications] Notification action performed:', action);
        handleNotificationClick(action.notification);
      });
    };

    setupListeners();
    initPushNotifications();

    // Cleanup listeners on unmount
    return () => {
      PushNotifications.removeAllListeners();
    };

  }, [user, userRole, registerToken, handleNotificationClick]);

  // Return nothing - this hook just sets up side effects
  return null;
}

export default usePushNotifications;
